import * as Yup from "yup";

import {
  AdditionalBuildingArealLimit,
  AdditionalBuildingFormValues,
  AdditionalBuildingStandard,
  AdditionalBuildingType,
  AnnexArealLimit,
} from "./additionalBuildingsInterfaces";

import { TextManager } from "Components/TextContext";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { mapFromFMSBuildingType } from "./additionalBuildingsUtils";

const arealLargerThanBoa = (areal: string, boa: string): boolean => {
  return parseInt(areal) >= parseInt(boa);
};

const isAnnex = (type: string | undefined): boolean => {
  return mapFromFMSBuildingType(type) === AdditionalBuildingType.ANNEX;
};

export const additionalBuildingValidationSchema =
  (): Yup.ObjectSchema<AdditionalBuildingFormValues> => {
    const { getText } = TextManager;

    return Yup.object({
      additionalBuilding_buildingType: Yup.string().required(
        getText("additionalbuildings.validation.buildingtype-required"),
      ),
      additionalBuilding_areal: Yup.string()
        .required(getText("additionalbuildings.validation.bta"))
        .test("arealLimit", function (value) {
          const annex = isAnnex(this.parent.additionalBuilding_buildingType);
          const areal = parseInt(value || "0");
          const limitLower = annex
            ? AnnexArealLimit.LOWER
            : AdditionalBuildingArealLimit.LOWER;
          const limitUpper = annex
            ? AnnexArealLimit.UPPER
            : AdditionalBuildingArealLimit.UPPER;
          const validRange = areal >= limitLower && areal <= limitUpper;

          return validRange
            ? true
            : this.createError({
                message: getText(
                  "additionalbuildings.validation.bta-validrange",
                  {
                    variables: {
                      additionalBuildings_limit_lower: limitLower,
                      additionalBuildings_limit_upper: limitUpper,
                    },
                  },
                ),
                path: "additionalBuilding_areal",
              });
        }),
      additionalBuilding_constructionYear: Yup.string()
        .required(
          getText("additionalbuildings.validation.constructionyear-required"),
        )
        .min(
          4,
          getText(
            "additionalbuildings.validation.constructionyear-validnumber",
          ),
        )
        .test(
          "constructionYear",
          getText("additionalbuildings.validation.constructionyear-validdate"),
          (value) => {
            const date = parseInt(value || "0");
            return date <= new Date().getFullYear() && date >= 1900;
          },
        ),
      additionalBuilding_isolated: Yup.boolean(),
      additionalBuilding_boa: Yup.string().when(
        "additionalBuilding_buildingType",
        {
          is: (type: string | undefined) => isAnnex(type),
          then: (schema) =>
            schema
              .required(getText("additionalbuildings.validation.bra"))
              .test("boaLimit", function (value) {
                const areal = parseInt(value || "0");
                const validRange =
                  areal >= AnnexArealLimit.LOWER &&
                  areal <= AnnexArealLimit.UPPER;
                return validRange
                  ? validRange
                  : this.createError({
                      message: getText(
                        "additionalbuildings.validation.bra-validrange",
                        {
                          variables: {
                            additionalBuildings_limit_lower:
                              AnnexArealLimit.LOWER,
                            additionalBuildings_limit_upper:
                              AnnexArealLimit.UPPER,
                          },
                        },
                      ),
                      path: "additionalBuilding_boa",
                    });
              })
              .test(
                "arealLargerThanBoa",
                getText("additionalbuildings.validation.bra-comparebta"),
                function (value) {
                  return (
                    value &&
                    this.parent.additionalBuilding_areal &&
                    arealLargerThanBoa(
                      this.parent.additionalBuilding_areal,
                      value,
                    )
                  );
                },
              ),
        },
      ),
      additionalBuilding_hasTapWater: Yup.mixed<BooleanFormType>().when(
        "additionalBuilding_buildingType",
        {
          is: (type: string | undefined) => isAnnex(type),
          then: (schema) =>
            schema.required("additionalbuildings.validation.hastapwater"),
        },
      ),
      additionalBuilding_hasElectricity: Yup.mixed<BooleanFormType>().when(
        "additionalBuilding_buildingType",
        {
          is: (type: string | undefined) => isAnnex(type),
          then: (schema) =>
            schema.required("additionalbuildings.validation.haselectricity"),
        },
      ),
      additionalBuilding_standard: Yup.mixed<AdditionalBuildingStandard>().when(
        "additionalBuilding_buildingType",
        {
          is: (type: string | undefined) => isAnnex(type),
          then: (schema) =>
            schema.required(
              "additionalbuildings.validation.additionalbuilding_standard-required",
            ),
        },
      ),
    }).defined();
  };
